import React, {
  createContext,
  startTransition,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { doGet } from "../utils/Apis";
import { readReferral, saveReferral, clearReferral } from "./referralStorage";

/**
 * App-wide referral/invite-code capture.
 *
 * Captures a code from the landing URL (?ref= or ?code=) the moment the app sees
 * it, persists it (referralStorage: localStorage + cookie), validates it against
 * the existing /user/invite-code/preview endpoint, and exposes it to the signup
 * form (which prefills + submits it) and the banner (which advertises the bonus).
 *
 * Because the code lives in durable storage rather than transient page/URL state,
 * it survives navigation today and will survive the social-login OAuth redirect
 * once that exists: the callback just calls `consume()` to read + clear it.
 */

// Fallback base free-trial length, used only when there is no validated offer
// (the plain signup with no invite). When a valid preview IS present the backend
// is the source of truth via `total_trial_days` (see trialDaysFrom), so the only
// drift risk is this no-offer default -- keep it in sync with the backend's
// DEFAULT_TRIAL_DAYS, or have the preview always send total_trial_days to remove
// the duplication entirely.
export const BASE_TRIAL_DAYS = 14;
// How long the code must stay unchanged before it is checked with the backend.
export const PREVIEW_DEBOUNCE_MS = 300;

// Bonus days a (validated) preview grants on top of the base trial. Centralised
// so the title, subtitle and the invite kicker can never show inconsistent
// day counts.
export const bonusDaysFrom = (preview) =>
  preview?.valid ? preview?.trial_days || 0 : 0;

// Total effective trial length for a given preview. Prefers a backend-provided
// total (single source of truth); falls back to base + bonus, then to the base
// default when there is no valid offer.
export const trialDaysFrom = (preview) => {
  if (preview?.valid && Number.isFinite(preview?.total_trial_days)) {
    return preview.total_trial_days;
  }
  return BASE_TRIAL_DAYS + bonusDaysFrom(preview);
};

const ReferralContext = createContext({
  code: "",
  preview: null,
  setCode: () => {},
  clear: () => {},
  consume: () => "",
});

// ?ref= is the marketing-link form; ?code= is the existing invite/deep-link
// form. Prefer ?ref= when both are present.
function codeFromSearch(search) {
  const params = new URLSearchParams(search);
  return (params.get("ref") || params.get("code") || "").trim();
}

export const ReferralProvider = ({ children }) => {
  const location = useLocation();
  const [code, setCodeState] = useState("");
  const [preview, setPreview] = useState(null);
  const debounceRef = useRef(null);
  const lastPreviewed = useRef(null);

  // Capture-on-arrival. A code in the URL is the strongest signal of intent, so
  // it overwrites any previously stored code. With no URL code, hydrate from
  // storage so a code captured on an earlier page survives navigation. Taken
  // as a transition, as AuthContext.jsx resolves the session, so the parts of
  // a built page whose script is still on its way keep their markup.
  useEffect(() => {
    const urlCode = codeFromSearch(location.search);
    if (urlCode) {
      if (urlCode !== code) {
        saveReferral(urlCode, "url");
        startTransition(() => setCodeState(urlCode));
      }
      return;
    }
    if (!code) {
      const stored = readReferral();
      if (stored) startTransition(() => setCodeState(stored));
    }
    // Capture keys off the URL only; `code` is read as a closure value on
    // purpose (we don't want re-runs when the code changes for other reasons).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Validate (debounced) whenever the code changes, so the banner/form show the
  // real bonus and reject invalid codes.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const trimmed = code.trim();
    if (!trimmed) {
      setPreview(null);
      return;
    }
    if (lastPreviewed.current === trimmed && preview) return;
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await doGet(
          `/user/invite-code/preview?code=${encodeURIComponent(trimmed)}`
        );
        lastPreviewed.current = trimmed;
        if (res?.status === 200 && res.data) {
          setPreview(res.data);
        } else {
          // A non-200 is NOT a verified "invalid code" -- treat it as unverified
          // and log so a preview outage is diagnosable from logs alone. The code
          // is still submitted (the backend re-validates), so the user keeps any
          // real bonus even when we can't preview it here.
          console.warn(
            `[referral] invite-code preview returned status ${res?.status} for "${trimmed}"; treating as unverified`
          );
          setPreview({ valid: false, reason: "lookup_failed" });
        }
      } catch (e) {
        // Backend momentarily unavailable: degrade to an unverified state (never
        // a hung spinner) but log with the code so the failure is diagnosable.
        console.error(
          `[referral] invite-code preview request failed for "${trimmed}":`,
          e
        );
        lastPreviewed.current = trimmed;
        setPreview({ valid: false, reason: "lookup_failed" });
      }
    }, PREVIEW_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  // Manual setter for the signup field: state + persistence move in lockstep.
  const setCode = useCallback((next) => {
    const raw = next || "";
    setCodeState(raw);
    const trimmed = raw.trim();
    if (trimmed) saveReferral(trimmed, "manual");
    else clearReferral();
  }, []);

  const clear = useCallback(() => {
    clearReferral();
    setCodeState("");
    setPreview(null);
    lastPreviewed.current = null;
  }, []);

  // For the future social-signup callback: read the code to attach to the newly
  // created user, then drop it from storage so it can't be re-applied.
  const consume = useCallback(() => {
    const current = code.trim() || readReferral() || "";
    clearReferral();
    return current;
  }, [code]);

  return (
    <ReferralContext.Provider
      value={{ code, preview, setCode, clear, consume }}
    >
      {children}
    </ReferralContext.Provider>
  );
};

export const useReferral = () => useContext(ReferralContext);

export default ReferralContext;
